/**
 * @file types.ts
 * @description Type definitions for the gamification system.
 * @module lib/gamification
 * 
 * Based on GAMIFICATION_PLAN.md - Self-Determination Theory principles:
 * - Autonomy: Athletes can customize which features they see
 * - Competence: Progress tracking without arbitrary rewards
 * - Relatedness: Optional social features (future)
 */

// ============================================================================
// SETTINGS & STATE
// ============================================================================

/**
 * Granular settings for which gamification features are visible.
 * All default to true when gamification is enabled.
 */
export interface GamificationSettings {
    /** Show training streaks on dashboard */
    showStreaks: boolean;
    /** Show achievement badges and progress */
    showAchievements: boolean;
    /** Show milestone celebrations */
    showMilestones: boolean;
    /** Show adherence score */
    showAdherenceScore: boolean;
    /** Show training insights */
    showInsights: boolean;
    /** Enable sharing to external platforms (future) */
    shareEnabled: boolean;
}

/**
 * Default settings when gamification is enabled
 */
export const DEFAULT_GAMIFICATION_SETTINGS: GamificationSettings = {
    showStreaks: true,
    showAchievements: true,
    showMilestones: true,
    showAdherenceScore: true,
    showInsights: true,
    shareEnabled: false,
};

/**
 * Complete gamification state for an athlete profile.
 */
export interface GamificationState {
    /** Master toggle - when false, all gamification is hidden */
    enabled: boolean;

    /** Granular feature toggles */
    settings: GamificationSettings;

    /** Unlocked achievements */
    achievements: Achievement[];

    /** Reached milestones */
    milestones: {
        volume: VolumeMilestone[];
        consistency: ConsistencyMilestone[];
        phase: PhaseMilestone[];
    };

    /** Streak tracking */
    streaks: {
        current: number;
        longest: number;
        currentCompliance: number;
        /** Consecutive weeks with integrity > 85 */
        integrity: number;
        history: StreakRecord[];
    };

    /** Training age and totals */
    trainingAge: {
        startDate: string;
        totalWorkouts: number;
        totalVolumeKm: number;
        totalTSS: number;
        /** Count of weeks reaching 95%+ integrity */
        totalMasterWeeks: number;
    };

    /** Current adherence score (0-100) */
    adherenceScore: number;
}

// ============================================================================
// ACHIEVEMENTS
// ============================================================================

// Forced refresh to fix export issue
export type AchievementRarity = 'common' | 'rare' | 'epic' | 'legendary';
export type AchievementCategory = 'training' | 'consistency' | 'process';

export type Achievement = {
    /** Unique identifier (e.g., 'zone_discipline') */
    id: string;
    /** Display name */
    name: string;
    /** Description of how to unlock */
    description: string;
    /** Rarity level */
    rarity: AchievementRarity;
    /** Category for grouping */
    category: AchievementCategory;
    /** ISO timestamp when unlocked (undefined = locked) */
    unlockedAt?: string;
    /** Progress toward unlock (0-100, for progressive achievements) */
    progress?: number;
    /** Current progress value for display (e.g., 13 in "13/30") */
    progressCurrent?: number;
    /** Target progress value for display (e.g., 30 in "13/30") */
    progressTarget?: number;
    /** Human readable condition for display (e.g., "Run 100km") */
    condition?: string;
};

/**
 * Achievement definition from the library (not yet unlocked)
 */
export interface AchievementDefinition extends Omit<Achievement, 'unlockedAt' | 'progress'> {
    /** Function to check if achievement should be unlocked */
    checkUnlock?: (context: AchievementContext) => boolean;
    /** Function to calculate progress (0-100) */
    calculateProgress?: (context: AchievementContext) => number;
}

export interface AchievementContext {
    totalWorkouts: number;
    consecutiveWeeks: number;
    zoneCompliance: number;
    completedPhases: string[];
    restDaysTaken: number;
    // Add more context as needed
    currentStreak: number;
    activeWeeks: number;
    weeksInBase: number;
    totalIntervalWorkouts: number;
    consecutiveLongRuns: number;

    // --- Integrity Specific ---
    /** Current weekly integrity score (0-100) */
    weeklyIntegrityScore: number;
    /** Consecutive weeks with integrity score > 85 */
    integrityStreakWeeks: number;
    /** Total number of "Scientific Master" weeks */
    totalMasterWeeks: number;
}

// ============================================================================
// MILESTONES
// ============================================================================

export interface VolumeMilestone {
    id: string; // generated from value + unit
    threshold: number;
    unit: 'km' | 'tss';
    name: string;
    icon?: string;
    message?: string;
    reachedAt?: string;
}

export interface ConsistencyMilestone {
    id: string;
    threshold: number;
    unit: 'days' | 'weeks' | 'percent' | 'month' | 'months';
    name: string;
    icon?: string;
    message?: string;
    reachedAt?: string;
}

export interface PhaseMilestone {
    id?: string;
    phase: 'Base' | 'Build' | 'Peak' | 'Taper' | 'Race' | 'Recovery';
    name: string;
    icon?: string;
    message?: string;
    reachedAt?: string;
}

// ============================================================================
// STREAKS
// ============================================================================

export interface StreakRecord {
    startDate: string;
    endDate: string;
    days: number;
    type: 'training' | 'compliance';
}

export type StreakType = 'building' | 'strong' | 'legendary';

export interface EnhancedStreak {
    /** Consecutive planned training days completed */
    currentStreak: number;
    /** All-time record */
    longestStreak: number;
    /** Consecutive weeks meeting plan compliance */
    currentComplianceStreak: number;
    /** Visual indicator tier */
    streakType: StreakType;
    /** True if streak correctly includes/excludes recovery days */
    includesRecoveryDays: boolean;
    /** Human-readable streak message */
    message: string;
}

// ============================================================================
// INSIGHTS
// ============================================================================

export interface TrainingInsight {
    id: string;
    type: 'encouragement' | 'observation' | 'tip';
    title: string;
    message: string;
    createdAt: string;
    /** Optional metric reference */
    metric?: {
        name: string;
        value: number;
        change?: number;
        unit: string;
    };
}

// ============================================================================
// DEFAULT STATE
// ============================================================================

export const DEFAULT_GAMIFICATION_STATE: GamificationState = {
    enabled: true,
    settings: { ...DEFAULT_GAMIFICATION_SETTINGS },
    achievements: [],
    milestones: {
        volume: [],
        consistency: [],
        phase: [],
    },
    streaks: {
        current: 0,
        longest: 0,
        currentCompliance: 0,
        integrity: 0,
        history: [],
    },
    trainingAge: {
        startDate: '',
        totalWorkouts: 0,
        totalVolumeKm: 0,
        totalTSS: 0,
        totalMasterWeeks: 0,
    },
    adherenceScore: 0,
};
